import { ArrowLeft, Home, MapPin, MoreHorizontal, Pencil } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/catalyst/button';
import { cn } from '@/lib/utils';
import type { ProviderDirectoryV2Service } from '@/services/types/provider-directory-v2-types';

interface ProviderHeroProps {
  service: ProviderDirectoryV2Service;
  onEditProvider?: () => void;
  onOverflowMenu?: () => void;
}

const formatAddress = (
  addr?: ProviderDirectoryV2Service['clinical_location'],
) => {
  if (!addr?.address) return null;
  const parts = [
    addr.address.address_line1,
    addr.address.city,
    addr.address.state,
    addr.address.postal_code,
  ].filter(Boolean);
  return parts.length > 0
    ? parts.join(', ')
    : (addr.address.formatted_address ?? null);
};

const formatRelativeTime = (iso: string | null | undefined) => {
  if (!iso) return null;
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const days = Math.floor(diffMs / 86_400_000);
  if (days < 1) return 'today';
  if (days === 1) return '1 day ago';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
};

/** Derive aggregate verification state from contact points. */
const deriveVerification = (
  contacts: ProviderDirectoryV2Service['contact_points'],
): 'verified' | 'pending' | 'unverified' => {
  if (!contacts || contacts.length === 0) return 'unverified';
  if (
    contacts.some(
      (c) => c.verification_status === 'VERIFIED' || c.verified_at,
    )
  )
    return 'verified';
  if (contacts.some((c) => c.verification_status === 'PENDING'))
    return 'pending';
  return 'unverified';
};

const VERIFICATION_CONFIG = {
  verified: {
    dotClass: 'bg-success',
    textClass: 'text-success',
    bgClass: 'bg-[#EDFFF0]',
    label: 'Verified',
  },
  pending: {
    dotClass: 'bg-warning',
    textClass: 'text-warning',
    bgClass: 'bg-[#FFF3ED]',
    label: 'Pending',
  },
  unverified: {
    dotClass: 'bg-zinc-400',
    textClass: 'text-zinc-500',
    bgClass: 'bg-zinc-100',
    label: 'Unverified',
  },
} as const;

export const ProviderHero = ({
  service,
  onEditProvider,
  onOverflowMenu,
}: ProviderHeroProps) => {
  const navigate = useNavigate();
  const address = formatAddress(service.clinical_location);
  const org = service.clinical_location?.organization;
  const verification = deriveVerification(service.contact_points);
  const vConfig = VERIFICATION_CONFIG[verification];
  const lastUsed = formatRelativeTime(service.last_used_at);

  // Build provenance line parts
  const provenanceParts: string[] = [];
  const verifiedContacts = (service.contact_points ?? []).filter(
    (c) => c.verification_status === 'VERIFIED' || c.verified_at,
  );
  if (verifiedContacts.length > 0 && verifiedContacts[0].verified_at) {
    const ago = formatRelativeTime(verifiedContacts[0].verified_at);
    if (ago) provenanceParts.push(`Last verified ${ago}`);
  }
  if (
    verifiedContacts.length > 0 &&
    verifiedContacts[0].source_type
  ) {
    const src = verifiedContacts[0].source_type
      .replace(/_/g, ' ')
      .toLowerCase();
    provenanceParts.push(`Source: ${src}`);
  }
  if (lastUsed) {
    provenanceParts.push(`Last used ${lastUsed}`);
  }

  // Service type for breadcrumb
  const serviceType =
    service.service_type && service.service_type !== 'GENERAL'
      ? service.service_type
          .replace(/_/g, ' ')
          .toLowerCase()
          .replace(/\b\w/g, (m) => m.toUpperCase())
      : null;

  return (
    <div>
      {/* Back link */}
      <button
        type="button"
        onClick={() => navigate('/provider-directory')}
        className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-primary hover:text-primary-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
      >
        <ArrowLeft size={16} />
        Back to search results
      </button>

      {/* Hero card */}
      <div className="rounded-2xl border border-border bg-background p-7 sm:p-8">
        <div className="flex items-start gap-6">
          {/* Left content */}
          <div className="flex-1 min-w-0 flex flex-col gap-3">
            {/* Row 1: Name + Verification pill */}
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-serif text-[28px] font-semibold text-info-dark leading-[34px] tracking-tight">
                {service.name}
              </h1>
              <span
                className={cn(
                  'inline-flex items-center gap-[5px] rounded-full px-2.5 py-1 text-[11px] font-medium',
                  vConfig.bgClass,
                  vConfig.textClass,
                )}
              >
                <span
                  className={cn(
                    'h-[7px] w-[7px] rounded-full shrink-0',
                    vConfig.dotClass,
                  )}
                />
                {vConfig.label}
              </span>
            </div>

            {/* Row 2: Address */}
            {address && (
              <p className="flex items-center gap-1.5 text-sm text-zinc-600">
                <MapPin size={14} className="shrink-0 text-info-light" />
                {address}
              </p>
            )}

            {/* Row 3: Org breadcrumb — Home icon + org name (primary link) + / + service type */}
            {org && (
              <p className="flex items-center gap-1.5 text-sm">
                <Home size={14} className="shrink-0 text-info-light" />
                <span className="font-medium text-primary">
                  {org.name}
                </span>
                {serviceType && (
                  <>
                    <span className="text-zinc-500">/</span>
                    <span className="text-zinc-600">{serviceType}</span>
                  </>
                )}
              </p>
            )}

            {/* Row 4: Feature badges */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Service type badge */}
              {serviceType && (
                <span className="inline-flex items-center rounded-full bg-[#F1EFFF] px-2.5 py-[3px] text-[11px] font-semibold uppercase tracking-[0.03em] text-primary">
                  {serviceType}
                </span>
              )}

              {/* Portal type badges */}
              {service.portal_type && service.portal_type.length > 0 ? (
                service.portal_type.map((pt) => (
                  <span
                    key={pt}
                    className="inline-flex items-center rounded-full bg-[#F1EFFF] px-2.5 py-[3px] text-[11px] font-semibold text-primary"
                  >
                    {pt === 'chartswap' ? 'ChartSwap' : pt === 'smart_request' ? 'SmartRequest' : pt === 'mro' ? 'MRO' : pt}
                  </span>
                ))
              ) : service.portal_eligible ? (
                <span className="inline-flex items-center rounded-full bg-[#F1EFFF] px-2.5 py-[3px] text-[11px] font-semibold text-primary">
                  Portal eligible
                </span>
              ) : null}

              {/* Active request plan */}
              {service.clinical_location?.has_active_request_plan && (
                <span className="inline-flex items-center rounded-full bg-[#EDFFF0] px-2.5 py-[3px] text-[11px] font-medium text-success">
                  Active request plan
                </span>
              )}
            </div>

            {/* Row 5: Provenance line */}
            {provenanceParts.length > 0 && (
              <p className="text-xs text-zinc-500">
                {provenanceParts.join(' · ')}
              </p>
            )}
          </div>

          {/* Right actions */}
          <div className="flex flex-col items-end gap-2 shrink-0">
            {onEditProvider && (
              <Button
                color="dark/white"
                onClick={onEditProvider}
              >
                <Pencil size={14} />
                Edit provider
              </Button>
            )}
            {onOverflowMenu && (
              <button
                type="button"
                onClick={onOverflowMenu}
                className="text-zinc-500 hover:text-info-dark p-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                aria-label="More actions"
              >
                <MoreHorizontal size={20} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
